import './table-pagination.css'
import React from 'react'
import {
  Input,
  CustomInput,
  Button
} from 'reactstrap'
import PropTypes from 'prop-types'

/**
 * Table pagination hooked up to ReactTable (only to be used for ReactTable)
 * It contain table pagination section
 *
 * @param {bool} canPreviousPage If there are pages and the current pageIndex is greater than 0, this will be `true`.
 * @param {bool} canNextPage If there are pages and the current pageIndex is less than pageCount, this will be `true`.
 * @param {number} pageIndex The index of the page that should be displayed. It from `state.pageIndex`
 * @param {number} pageSize number of records that should be displayed in a page. It from `state.pageSize`
 * @param {number} pageCount Determine the amount of pages available. Required if manualPagination is set to `true`
 * @param {func} gotoPage This function, when called with a valid `pageIndex`, will set `pageIndex` to that value.
 * @param {func} setPageSize Set `state.pageSize` to the new value.
 * @param {func} nextPage Increases `pageIndex` by one.
 * @param {func} previousPage Decreases `pageIndex` by one.
 *
 * for more detail, see https://react-table.tanstack.com/docs/api/usePagination
 */
const TablePagination = ({
  canPreviousPage,
  canNextPage,
  pageIndex,
  pageSize,
  pageCount,
  gotoPage,
  setPageSize,
  nextPage,
  previousPage
}) => {

  const onChangeInSelect = (event) => {
    setPageSize(Number(event.target.value))
  }

  const onChangeInput = (event) => {
    const requested = Number(event.target.value)
    if (!Number.isInteger(requested) || requested < 1 || pageCount < 1) return
    gotoPage(Math.min(requested, pageCount) - 1)
  }

  return (
    <div className='table-pagination-controls' aria-label='Table pagination'>

      <div className='table-pagination-nav'>
        <Button
          color="primary"
          aria-label='First page'
          onClick={ () => gotoPage(0) }
          disabled={ !canPreviousPage }
        >
          { '<<' }
        </Button>
        <Button
          color="primary"
          aria-label='Previous page'
          onClick={ previousPage }
          disabled={ !canPreviousPage }
        >
          { '<' }
        </Button>
      </div>

      <div className='table-pagination-center'>
        <div className='table-pagination-page'>
          <div>Page</div>
          <Input
            type='number'
            className='table-pagination-input'
            min={ 1 }
            max={ Math.max(1, pageCount || 0) }
            aria-label='Page number'
            disabled={ !pageCount }
            value={ pageCount ? pageIndex + 1 : 1 }
            onChange={ onChangeInput }
          />
          <div style={{ whiteSpace: 'nowrap' }}>of { Math.max(1, pageCount || 0) }</div>
        </div>
        <div>
          <CustomInput
            id='table-pagination-page-size'
            aria-label='Rows per page'
            type='select'
            value={ pageSize }
            onChange={ onChangeInSelect }
          >
            { [ 5, 10, 20, 30, 40, 50 ].map((pageSize) => (
              <option key={ pageSize } value={ pageSize }>
            Show { pageSize }
              </option>
            )) }
          </CustomInput>
        </div>
      </div>

      <div className='table-pagination-nav'>
        <Button
          color="primary"
          aria-label='Next page'
          onClick={ nextPage }
          disabled={ !canNextPage }
        >
          { '>' }
        </Button>
        <Button
          color="primary"
          aria-label='Last page'
          onClick={ () => gotoPage(pageCount - 1) }
          disabled={ !canNextPage }
        >
          { '>>' }
        </Button>
      </div>
    </div>
  )
}

TablePagination.propTypes = {
  canPreviousPage : PropTypes.bool,
  canNextPage     : PropTypes.bool,
  pageIndex       : PropTypes.number.isRequired,
  pageSize        : PropTypes.number,
  pageCount       : PropTypes.number,
  gotoPage        : PropTypes.func,
  setPageSize     : PropTypes.func,
  nextPage        : PropTypes.func,
  previousPage    : PropTypes.func
}

export default TablePagination